import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Users, Calendar, Building2, Video, Crown, Star, Shield, Check, ArrowRight } from 'lucide-react';
import { subscriptionPlans, createCheckoutSession, redirectToCheckout, formatPrice } from '../lib/stripe/subscriptions';

export function WelcomePopup({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activePlan, setActivePlan] = useState<'monthly' | 'yearly'>('yearly');

  const handleSubscribe = async (planId: 'monthly' | 'yearly') => {
    try {
      setLoading(true);
      setError(null);

      const sessionId = await createCheckoutSession(planId);
      await redirectToCheckout(sessionId);
    } catch (err: any) {
      console.error('Subscription error:', err);
      setError(err.message || t('subscription.failedStart'));
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: Video, titleKey: 'subscription.liveStreamAccess', descKey: 'subscription.liveStreamDesc' },
    { icon: Users, titleKey: 'subscription.createListings', descKey: 'subscription.createListingsDesc' },
    { icon: Calendar, titleKey: 'subscription.hostEvents', descKey: 'subscription.hostEventsDesc' },
    { icon: Building2, titleKey: 'subscription.buildCommunities', descKey: 'subscription.buildCommunitiesDesc' },
    { icon: Star, titleKey: 'subscription.advancedFeatures', descKey: 'subscription.advancedFeaturesDesc' },
    { icon: Shield, titleKey: 'subscription.prioritySupport', descKey: 'subscription.prioritySupportDesc' }
  ];

  const activePlanData = subscriptionPlans.find(p => p.id === activePlan);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60] px-4 py-16 sm:p-6">
      <div className="bg-[#FAFCF7] w-full sm:max-w-2xl lg:max-w-4xl rounded-2xl shadow-2xl max-h-full overflow-y-auto">

        {/* Header with decorative background */}
        <div className="relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-accent-text/10 via-[#F3F7EE] to-accent-text/5" />
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-accent-text/10 rounded-full blur-2xl" />
          <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-accent-text/8 rounded-full blur-2xl" />

          <button
            onClick={onClose}
            className="absolute top-4 right-4 z-10 p-2 text-content/40 hover:text-content/70 rounded-full hover:bg-white/60 transition-colors"
          >
            <X className="h-5 w-5" />
          </button>

          <div className="relative px-6 pt-8 pb-6 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-accent-text/15 rounded-2xl mb-5">
              <Crown className="h-8 w-8 text-accent-text" />
            </div>
            <h2 className="text-2xl sm:text-3xl font-gelica font-bold text-content mb-2">
              {t('subscription.welcomeTitle')}
            </h2>
            <p className="text-content/60 text-[15px] max-w-md mx-auto">
              {t('subscription.welcomeSubtitle')}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="px-5 sm:px-6 pb-6">

          {/* Features grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={index}
                  className="flex items-start gap-3 bg-white rounded-xl p-3.5 border border-gray-100/80"
                >
                  <div className="flex-shrink-0 w-9 h-9 bg-accent-text/10 rounded-lg flex items-center justify-center">
                    <Icon className="h-4.5 w-4.5 text-accent-text" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-content text-sm leading-tight">{t(feature.titleKey)}</h3>
                    <p className="text-content/50 text-xs mt-0.5 leading-snug">{t(feature.descKey)}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Plan Toggle */}
          <div className="bg-white rounded-2xl border border-gray-100 p-1.5 flex mb-5">
            {subscriptionPlans.map((plan) => (
              <button
                key={plan.id}
                onClick={() => setActivePlan(plan.id)}
                className={`relative flex-1 py-3 px-4 rounded-xl text-center transition-all duration-200 ${
                  activePlan === plan.id
                    ? 'bg-accent-text text-white shadow-md'
                    : 'text-content/60 hover:text-content'
                }`}
              >
                {plan.savings && (
                  <span className={`absolute -top-2.5 left-1/2 -translate-x-1/2 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                    activePlan === plan.id
                      ? 'bg-white text-accent-text'
                      : 'bg-green-100 text-green-700'
                  }`}>
                    {plan.savings}
                  </span>
                )}
                <div className={`text-xs font-medium mb-0.5 ${activePlan === plan.id ? 'text-white/80' : ''}`}>
                  {plan.name}
                </div>
                <div className="flex items-baseline justify-center gap-0.5">
                  <span className={`text-xl font-bold ${activePlan === plan.id ? 'text-white' : 'text-content'}`}>
                    {formatPrice(plan.price, plan.currency)}
                  </span>
                  <span className={`text-xs ${activePlan === plan.id ? 'text-white/70' : 'text-content/40'}`}>
                    /{plan.interval}
                  </span>
                </div>
              </button>
            ))}
          </div>

          {/* Yearly savings note */}
          {activePlan === 'yearly' && (
            <div className="flex items-center justify-center gap-2 mb-5 py-2.5 px-4 bg-green-50 rounded-xl border border-green-100">
              <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
              <span className="text-sm text-green-700 font-medium">{t('subscription.saveCompared')}</span>
            </div>
          )}

          {/* CTA Button */}
          <button
            onClick={() => activePlanData && handleSubscribe(activePlanData.id)}
            disabled={loading}
            className="w-full py-4 px-6 bg-accent-text text-white font-semibold text-base rounded-xl hover:bg-accent-text/90 active:scale-[0.98] transition-all duration-200 shadow-lg shadow-accent-text/20 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                {t('subscription.processing')}
              </>
            ) : (
              <>
                {t('subscription.choosePlan', { plan: activePlanData?.name || '' })}
                <ArrowRight className="h-4.5 w-4.5" />
              </>
            )}
          </button>

          {/* Maybe Later */}
          <button
            onClick={onClose}
            className="w-full mt-3 py-3 text-sm text-content/50 hover:text-content/70 transition-colors rounded-xl hover:bg-white"
          >
            {t('subscription.maybeLater')}
          </button>

          {/* Error */}
          {error && (
            <div className="mt-4 p-3.5 bg-red-50 border border-red-100 rounded-xl">
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          {/* Trust badges */}
          <div className="mt-4 flex items-center justify-center gap-2 text-content/30">
            <Shield className="h-3.5 w-3.5" />
            <span className="text-[11px] font-medium">{t('subscription.secureShort')}</span>
          </div>
        </div>
      </div>
    </div>
  );
}
